import { useState, useMemo } from 'react';
import { Search, Filter, AlertCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { DataTable } from '../components/tables/DataTable';
import { UserDetailPanel } from '../components/users';
import { cn } from '../lib/cn';
import { useAdminUsers, formatRelativeTime } from '../api/hooks';
import { getUserStatus, type AdminUserResponse, type UserStatus } from '../api/types';
import type { ColumnDef } from '@tanstack/react-table';

function StatusBadge({ status }: { status: UserStatus }) {
  return (
    <span
      className={cn(
        'badge',
        status === 'active' && 'badge-active',
        status === 'inactive' && 'badge-suspended',
        status === 'new' && 'badge-pending'
      )}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

const columns: ColumnDef<AdminUserResponse>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div>
        <p className="font-medium text-text">
          {row.original.name || 'No name'}
        </p>
        <p className="text-caption text-text-muted">
          {row.original.email}
        </p>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={getUserStatus(row.original)} />,
  },
  {
    accessorKey: 'company_name',
    header: 'Business',
    cell: ({ row }) => (
      <span className="text-text-secondary">
        {row.original.has_business ? (row.original.company_name || 'Unnamed Business') : '-'}
      </span>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span className="text-text-secondary">
        {formatRelativeTime(row.original.created_at)}
      </span>
    ),
  },
  {
    accessorKey: 'last_active_at',
    header: 'Last Active',
    cell: ({ row }) => (
      <span className="text-text-secondary">
        {row.original.last_active_at ? formatRelativeTime(row.original.last_active_at) : 'Never'}
      </span>
    ),
  },
  {
    accessorKey: 'load_count',
    header: 'Loads',
    cell: ({ row }) => (
      <span className="tabular-nums">{row.original.load_count}</span>
    ),
  },
];

function TableSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-12" />
        </div>
      ))}
    </div>
  );
}

/**
 * Admin users management page.
 * Lists all users with search, filter, and actions.
 */
export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Debounce search input
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data, isLoading, error } = useAdminUsers(
    debouncedSearch ? { search: debouncedSearch } : undefined
  );

  const handleRowClick = (user: AdminUserResponse) => {
    setSelectedUserId(user.id);
  };

  const handleClosePanel = () => {
    setSelectedUserId(null);
  };

  return (
    <>
      <Header
        title="Users"
        description={data ? `${data.total_count} total users` : 'Manage platform users'}
      />

      <div className="p-6">
        {/* Error State */}
        {error && (
          <Card padding="md" hover={false} className="mb-4 border-loss/50">
            <div className="flex items-center gap-3 text-loss">
              <AlertCircle size={20} />
              <span className="text-body-sm">
                Failed to load users. Please check your connection and try again.
              </span>
            </div>
          </Card>
        )}

        {/* Search and Filter Bar */}
        <Card padding="sm" hover={false} className="mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
              />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'w-full pl-9 pr-3 py-2 rounded-button border text-body-sm',
                  'bg-surface',
                  'border-border',
                  'text-text',
                  'placeholder:text-text-muted',
                  'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent'
                )}
              />
            </div>
            <Button variant="secondary" size="sm">
              <Filter size={16} />
              Filter
            </Button>
          </div>
        </Card>

        {/* Users Table */}
        <Card padding="none" hover={false}>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              data={data?.users ?? []}
              columns={columns}
              onRowClick={handleRowClick}
              enablePagination
              pageSize={10}
              emptyMessage={searchQuery ? 'No users match your search' : 'No users found'}
            />
          )}
        </Card>

        {/* Pagination Info */}
        {data && data.total_count > 0 && (
          <div className="mt-4 text-caption text-text-muted text-center">
            Showing {Math.min(data.users.length, data.limit)} of {data.total_count} users
          </div>
        )}
      </div>

      {/* User Detail Side Panel */}
      <UserDetailPanel userId={selectedUserId} onClose={handleClosePanel} />
    </>
  );
}
